import styles from './style.module.css';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Markup } from 'interweave';
import { Button } from '../../components/Button';
import { HelmetComponent } from '../../components/Helmet';
import { IFormValues } from './components/interfaces';
import { Notify } from '../../components/Notify';
import { Card } from '../../components/Card';
import { List, ListItem } from '../../components/List';
import { Input } from '../../components/forms/Input';
import { ModalConfirm } from './components/ModalConfirm';
import { api } from '../../server/api';
import { MaskCNPJ, MaskCPF, maskTEL } from '../../utils/mask';
import { useAuth } from '../../hooks/useAuth';
import pixImage from '../../assets/images/pix.png';

export function PaymentLead() {
  const { setClient, client } = useAuth();
  const { clientId, leadId } = useParams();

  const [paymentMethod, setPaymentMethod] = useState('');
  const [typePix, setTypePix] = useState('');
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<IFormValues>();

  useEffect(() => {
    getClientById();
  }, []);

  const getClientById = async () => {
    const parameters = {
      userType: 'checkoutLead',
      searchFunctionality: 'searchClient',
      clientId: clientId,
    };

    const { data } = await api.post('/checkout', parameters);
    if (data.success) {
      setClient(data.data);
    }
  };

  const handleForm = handleSubmit(async (dataForm) => {
    try {
      let formData: any = {};
      if (paymentMethod === 'pix') {
        formData = {
          accountData: {
            paymentMethod,
            pixkey: dataForm.pixkey,
            typePix,
          },
        };
      } else {
        formData = {
          accountData: {
            paymentMethod,
            name: dataForm.name,
            bank: dataForm.bank,
            accountType: dataForm.accountType,
            agency: dataForm.agencyNumber,
            conta: dataForm.accountNumber,
            digitCount: dataForm.digitAccount,
            digitAgency: dataForm.digitAgency,
            cpf: dataForm.cpf,
          },
        };
      }

      formData = {
        ...formData,
        typeRescue: 'rescueLead',
      };

      const parameters = {
        createFunctionality: 'paymentLead',
        userType: 'paymentLead',
        formData,
        clientId: client.id,
        leadId,
      };

      const { data } = await api.post('/checkout', parameters);
      if (data.success) {
        toggleModal();
      } else {
        return Notify(data.message, 'error');
      }
    } catch (error) {
      console.log('handleForm error', error);
    }
  });

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const handleChange = (type: string) => {
    //929.324.090-42 cpf 14
    //77.996.783/0001-30 cnpj 18
    //(86) 99979-7405 telefone 15
    //2772b87c-b909-4e42-903b-4eab0ff57fff chave aleatória 32
    if (type === 'CPF') {
      setTypePix('CPF');
      setMaxLength(14);
      setValue('pixkey', '');
    }
    if (type === 'CNPJ') {
      setTypePix('CNPJ');
      setMaxLength(18);
      setValue('pixkey', '');
    }
    if (type === 'TELEFONE') {
      setTypePix('TELEFONE');
      setMaxLength(15);
      setValue('pixkey', '');
    }
    if (type === 'CHAVE_ALEATORIA') {
      setTypePix('CHAVE_ALEATORIA');
      setMaxLength(36);
      setValue('pixkey', '');
    }
    if (type === 'EMAIL') {
      setTypePix('EMAIL');
      setMaxLength(undefined);
      setValue('pixkey', '');
    }
  };

  const renderPaymentPix = () => {
    return (
      <>
        <div className="mt-3">
          <span className="fs-4 fw-semibold">Selecione sua chave pix</span>
        </div>
        <div className="mt-3">
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <List>
                <ListItem
                  handleChange={() => handleChange('CPF')}
                  icon="person"
                  content="CPF"
                  rangeChecked={typePix === 'CPF'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('CNPJ')}
                  icon="business"
                  content="CNPJ"
                  rangeChecked={typePix === 'CNPJ'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('TELEFONE')}
                  icon="phone"
                  content="Telefone"
                  rangeChecked={typePix === 'TELEFONE'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('EMAIL')}
                  icon="mail"
                  content="E-mail"
                  rangeChecked={typePix === 'EMAIL'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('CHAVE_ALEATORIA')}
                  icon="format_color_text"
                  content="Chave aleatória"
                  rangeChecked={typePix === 'CHAVE_ALEATORIA'}
                  withRange={true}
                />
              </List>
            </div>
            <div className="col-sm-12 col-md-6">
              <div className="my-3">
                <Input
                  maxLength={maxLength}
                  type="text"
                  label="Chave PIX"
                  placeholder={
                    typePix === 'CHAVE_ALEATORIA'
                      ? 'Copie e cole aqui'
                      : 'Digite sua chave Pix'
                  }
                  disabled={!typePix}
                  readOnly={!typePix}
                  {...register('pixkey', {
                    required: true,
                    pattern: {
                      value:
                        typePix === 'EMAIL'
                          ? /^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i
                          : /./,
                      message:
                        typePix === 'EMAIL' ? 'invalid email address' : '',
                    },
                  })}
                  errors={errors.pixkey ? 'Campo obrigatório' : ''}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (typePix === 'CPF') {
                      if (value.length <= 14) {
                        event.target.value = MaskCPF(value);
                      }
                    }
                    if (typePix === 'CNPJ') {
                      if (value.length > 14) {
                        event.target.value = MaskCNPJ(value);
                      }
                    }
                    if (typePix === 'TELEFONE') {
                      event.target.value = maskTEL(value);
                    }
                    if (typePix === 'CHAVE_ALEATORIA') {
                      event.target.value = value;
                    }
                    if (typePix === 'EMAIL') {
                      event.target.value = value;
                    }
                  }}
                />
              </div>
              <div className="mt-3">
                <div className="alert alert-warning" role="alert">
                  <h4 className="alert-heading">Atenção ao resgatar</h4>
                  <br />
                  <ul>
                    <li>
                      Os dados de resgate via PIX ou TED, devem corresponder ao
                      mesmo CPF ou CNPJ do usuário cadastrado na plataforma.
                    </li>
                    <li>
                      Não é permitido utilizar contas de terceiros para realizar
                      o resgate.
                    </li>
                    <li>
                      Informe seus dados de resgate{' '}
                      <strong>corretamente</strong> e revise-os antes de
                      finalizar o resgate.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const verifyDisabled = () => {
    if (!paymentMethod) {
      return true;
    }

    if (paymentMethod === `pix` && !typePix) {
      return true;
    }
    return false;
  };

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={`Checkout`}
      />

      <div className="container">
        <div className="container">
          <div className="py-5 text-center">
            <img
              className="d-block mx-auto mb-4"
              src={client?.logoURL}
              alt=""
              height="80"
            />

            <p className="lead">
              Preencha os dados abaixo e os dados corretos para pagamento.
              Verifique os campos obrigatórios.
            </p>
          </div>

          <form onSubmit={handleForm}>
            <div className="row">
              <div className="col-sm-12">
                <div className="mb-3">
                  <Card>
                    <List>
                      <ListItem
                        handleChange={() => setPaymentMethod('pix')}
                        image={pixImage}
                        content="Pagamento via PIX"
                        rangeChecked={paymentMethod === 'pix'}
                        withRange={true}
                        inputType="radio"
                      />
                    </List>

                    {paymentMethod === 'pix' && renderPaymentPix()}

                    <div className="d-flex flex-row-reverse me-check justify-content-end mt-3">
                      <Input
                        type="checkbox"
                        label={
                          <Markup
                            content={`Li e concordo com a <a class="fw-bold"  target="_blank"
                          href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPOLI%CC%81TICA%20DE%20PRIVACIDADE%20LGPD%20COMPLIANCE%202026.pdf?alt=media&token=f6339049-9c75-4ac2-8417-a70c9d75be94">Política de Privacidade</a> e aceito os termos definidos.`}
                          />
                        }
                        {...register('acceptedTerms', {
                          required: true,
                        })}
                        style={{
                          marginRight: '15px',
                        }}
                      />
                    </div>

                    <span className={`${styles.errorAccount}`}>
                      {errors.acceptedTerms ? 'Campo obrigatório' : ''}
                    </span>
                    <div className="d-flex justify-content-center p-4">
                      <Button
                        type="submit"
                        text="Finalizar"
                        color="primary"
                        icon="check"
                        disabled={verifyDisabled() || isSubmitting}
                        isLoading={isSubmitting}
                      />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </form>

          <footer className="my-5 pt-5 text-muted text-center text-small">
            <p className="mb-1">&copy; 2021-2022 OQ Digital</p>
            <ul className="list-inline">
              <li className="list-inline-item">
                <a href="#">Privacidade</a>
              </li>
              <li className="list-inline-item">
                <a href="#">Termos</a>
              </li>
              <li className="list-inline-item">
                <a href="#">Suporte</a>
              </li>
            </ul>
          </footer>
        </div>
      </div>

      <ModalConfirm
        // modalIsOpen={true}
        modalIsOpen={modalIsOpen}
        toggleModal={toggleModal}
        clientUrl={client?.clientUrl ? client?.clientUrl : ''}
        clientNumberOfDaysToRedeem={
          client && client.numberOfDaysToRedeem
            ? `até ${client.numberOfDaysToRedeem} dias úteis`
            : 'alguns dias'
        }
      />
    </>
  );
}
